var m = require('mithril');
var prop = require('mithril/stream');
var key = require('../libs/keybindings');
var modal = require('../layout/components/modal');
var loaders = require('../layout/components/loaders');
var ToggleSwitch = require('../components/toggleSwitch');

var addDiagramEquipmentModal = {};

addDiagramEquipmentModal.searchValue = prop('');
addDiagramEquipmentModal.selectedEquipment = prop([]);
addDiagramEquipmentModal.results = prop([]);
addDiagramEquipmentModal.recordsToExclude = [];
addDiagramEquipmentModal.error = prop('');
addDiagramEquipmentModal.autoLayoutDatasets = prop(false);
addDiagramEquipmentModal.autoLayoutMarkers = prop(false);
addDiagramEquipmentModal.loading = prop(false);
addDiagramEquipmentModal._searchDebounce = null;
addDiagramEquipmentModal._requestCounter = 0;

function normalizeRid(value) {
  if (!value) return '';
  var rid = String(value).trim();
  if (!rid) return '';
  return rid.charAt(0) === '#' ? rid : ('#' + rid);
}

function isLocationRecord(record) {
  return ['Site', 'Building', 'Level', 'Room'].indexOf(record && record['@class']) >= 0;
}

function buildPrimaryLabel(record) {
  if (!record) return '';

  var name = record.name ? String(record.name).trim() : '';
  var roomNumber = record.roomNumber ? String(record.roomNumber).trim() : '';

  if (isLocationRecord(record)) {
    if (roomNumber && name) return roomNumber + ' - ' + name;
    if (roomNumber) return roomNumber;
    if (name) return name;
  }

  return name || roomNumber || record['@rid'] || '';
}

function buildPathLabel(record) {
  var path = Array.isArray(record && record['$path']) ? record['$path'].slice() : [];
  return path
    .map(function (segment) {
      return segment && (segment.name || segment.roomNumber || segment['@class']);
    })
    .filter(Boolean)
    .join(' / ');
}

function getDiagramOwnerRid(diagramRecord) {
  var ownerEdges = diagramRecord && diagramRecord.in_hasDiagram;
  if (Array.isArray(ownerEdges) && ownerEdges.length && ownerEdges[0] && ownerEdges[0].out) {
    return normalizeRid(ownerEdges[0].out['@rid']);
  }

  var path = Array.isArray(diagramRecord && diagramRecord.path) ? diagramRecord.path : [];
  if (path.length > 1) {
    return normalizeRid(path[path.length - 2] && path[path.length - 2]['@rid']);
  }

  return '';
}

function normalizeResultRecords(records) {
  var excluded = new Set((addDiagramEquipmentModal.recordsToExclude || []).map(normalizeRid).filter(Boolean));
  var byRid = new Map();
  (records || []).forEach(function (record) {
    var rid = normalizeRid(record && record['@rid']);
    if (!rid || excluded.has(rid) || byRid.has(rid)) return;
    byRid.set(rid, record);
  });
  return Array.from(byRid.values());
}

function isSelected(equipmentRecord) {
  var equipmentRid = normalizeRid(equipmentRecord && equipmentRecord['@rid']);
  return (addDiagramEquipmentModal.selectedEquipment() || []).some(function (selectedRecord) {
    return normalizeRid(selectedRecord && selectedRecord['@rid']) === equipmentRid;
  });
}

function toggleSelection(equipmentRecord) {
  var equipmentRid = normalizeRid(equipmentRecord && equipmentRecord['@rid']);
  if (!equipmentRid) return;

  var selectedRecords = (addDiagramEquipmentModal.selectedEquipment() || []).slice();
  var existingIndex = selectedRecords.findIndex(function (selectedRecord) {
    return normalizeRid(selectedRecord && selectedRecord['@rid']) === equipmentRid;
  });

  if (existingIndex >= 0) {
    selectedRecords.splice(existingIndex, 1);
  } else {
    selectedRecords.push(equipmentRecord);
  }

  addDiagramEquipmentModal.selectedEquipment(selectedRecords);
}

function removeSelection(equipmentRecord) {
  var equipmentRid = normalizeRid(equipmentRecord && equipmentRecord['@rid']);
  addDiagramEquipmentModal.selectedEquipment(
    (addDiagramEquipmentModal.selectedEquipment() || []).filter(function (selectedRecord) {
      return normalizeRid(selectedRecord && selectedRecord['@rid']) !== equipmentRid;
    })
  );
}

function searchEquipment(diagramRecord) {
  var requestId = ++addDiagramEquipmentModal._requestCounter;
  addDiagramEquipmentModal.loading(true);
  addDiagramEquipmentModal.error('');

  return loaders.requestWithLoader({
    loader: 'background',
    url: '/api/v1/entities/search',
    method: 'GET',
    background: true,
    params: {
      search: addDiagramEquipmentModal.searchValue(),
      families: 'Equipment,Zone',
      mode: 'paginated',
      pageSize: 25,
      locationRid: getDiagramOwnerRid(diagramRecord) || undefined
    }
  })
  .then(function (records) {
    if (requestId !== addDiagramEquipmentModal._requestCounter) return;
    addDiagramEquipmentModal.results(normalizeResultRecords(records));
  })
  .catch(function (error) {
    if (requestId !== addDiagramEquipmentModal._requestCounter) return;
    addDiagramEquipmentModal.results([]);
    addDiagramEquipmentModal.error(error && (error.message || error.error) || 'Unable to search equipment.');
  })
  .finally(function () {
    if (requestId === addDiagramEquipmentModal._requestCounter) {
      addDiagramEquipmentModal.loading(false);
      m.redraw();
    }
  });
}

addDiagramEquipmentModal.open = function (diagramRecord, options) {
  addDiagramEquipmentModal.diagramRecord = diagramRecord;
  if (addDiagramEquipmentModal._searchDebounce) {
    clearTimeout(addDiagramEquipmentModal._searchDebounce);
    addDiagramEquipmentModal._searchDebounce = null;
  }
  addDiagramEquipmentModal.searchValue('');
  addDiagramEquipmentModal.selectedEquipment([]);
  addDiagramEquipmentModal.results([]);
  addDiagramEquipmentModal.error('');
  addDiagramEquipmentModal.autoLayoutDatasets(false);
  addDiagramEquipmentModal.autoLayoutMarkers(false);
  addDiagramEquipmentModal.loading(false);
  addDiagramEquipmentModal.recordsToExclude = (diagramRecord.out_hasDiagramEquipment || [])
    .map(function (edge) {
      return edge && edge.in && edge.in['@rid'];
    })
    .filter(function (rid) {
      return !!rid;
    });

  var submit = function () {
    var selected = addDiagramEquipmentModal.selectedEquipment() || [];
    var equipmentRids = selected.map(function (equipmentRecord) {
      return normalizeRid(equipmentRecord && equipmentRecord['@rid']);
    }).filter(Boolean);
    if (!equipmentRids.length) {
      addDiagramEquipmentModal.error('Select equipment to add.');
      m.redraw();
      return;
    }

    return equipmentRids.reduce(function (chain, equipmentRid) {
      return chain.then(function () {
        return loaders.requestWithLoader({
          loader: 'main',
          url: '/api/v1/diagram/' + diagramRecord['@rid'].slice(1) + '/equipment',
          method: 'POST',
          body: {
            equipmentRid: equipmentRid,
            autoLayoutDatasets: addDiagramEquipmentModal.autoLayoutDatasets() === true,
            autoLayoutMarkers: addDiagramEquipmentModal.autoLayoutMarkers() === true
          }
        });
      });
    }, Promise.resolve())
    .then(function () {
      key.unbind('enter');
      modal.vm.close();
      if (options && typeof options.submitCallback === 'function') {
        options.submitCallback({
          equipmentRid: equipmentRids[equipmentRids.length - 1],
          equipmentRids: equipmentRids
        });
      }
    })
    .catch(function (error) {
      addDiagramEquipmentModal.error(error && (error.message || error.error) || 'Unable to add equipment.');
      m.redraw();
    });
  };

  key.setScope('modal');
  key('enter', 'modal', submit);
  modal.vm.title('Add Equipment to Diagram');
  modal.vm.body = addDiagramEquipmentModal.view;
  modal.vm.buttons([
    {
      label: 'Save',
      class: 'btn-good',
      func: submit
    }
  ]);
  modal.vm.open({ extrawidth: true });
  return searchEquipment(diagramRecord);
};

addDiagramEquipmentModal.view = function () {
  var selected = addDiagramEquipmentModal.selectedEquipment() || [];
  var results = addDiagramEquipmentModal.results() || [];
  return m('form', { role: 'form' }, [
    addDiagramEquipmentModal.error() ? m('div.red.mb2', addDiagramEquipmentModal.error()) : '',
    selected.length ? m('div.mb3', [
      m('div.f6.moon-gray.mb2', selected.length + ' selected'),
      m('div.flex.flex-wrap', selected.map(function (equipmentRecord) {
        return m('button.bn.br2.bg-dark-gray.white.mr2.mb2.ph2.pv1.pointer', {
          type: 'button',
          key: equipmentRecord['@rid'],
          onclick: function () {
            removeSelection(equipmentRecord);
          }
        }, [
          m('span.mr2', buildPrimaryLabel(equipmentRecord)),
          m('i.ion-close')
        ]);
      }))
    ]) : null,
    m('div.mb3', [
      m('label.db.mb2', { for: 'diagram-equipment-search' }, 'Search equipment'),
      m('input#diagram-equipment-search.w-100.ba.b--mid-gray.br2.pa2', {
        type: 'text',
        value: addDiagramEquipmentModal.searchValue(),
        placeholder: 'Name, room number, or class',
        oninput: function (ev) {
          addDiagramEquipmentModal.searchValue(ev.target.value);
          if (addDiagramEquipmentModal._searchDebounce) {
            clearTimeout(addDiagramEquipmentModal._searchDebounce);
          }
          addDiagramEquipmentModal._searchDebounce = setTimeout(function () {
            searchEquipment(addDiagramEquipmentModal.diagramRecord);
          }, 250);
        }
      })
    ]),
    addDiagramEquipmentModal.loading() ? m('div.moon-gray.mb2', 'Loading equipment...') : null,
    m('div.ba.b--mid-gray.br2.overflow-y-auto', { style: { maxHeight: '48vh' } }, [
      results.length
        ? results.map(function (equipmentRecord) {
            var selectedRecord = isSelected(equipmentRecord);
            return m('button.db.w-100.tl.pa3.bn.bg-transparent.bb.b--near-black.pointer', {
              type: 'button',
              key: equipmentRecord['@rid'],
              onclick: function () {
                toggleSelection(equipmentRecord);
              }
            }, [
              m('div.flex.justify-between.items-center', [
                m('div.pr3', [
                  m('div.f5' + (selectedRecord ? '.blue' : '.white'), buildPrimaryLabel(equipmentRecord)),
                  m('div.f6.moon-gray.mt1', buildPathLabel(equipmentRecord) || equipmentRecord['@class'])
                ]),
                m('i', {
                  class: selectedRecord ? 'ion-android-checkbox-outline blue f4' : 'ion-android-checkbox-outline-blank f4 moon-gray'
                })
              ])
            ]);
          })
        : (!addDiagramEquipmentModal.loading() ? m('div.pa3.moon-gray', 'No matching equipment found.') : null)
    ]),
    m('div.mt3 pt2 bt b--mid-gray', [
      m('div.flex.items-center.justify-between mb2', [
        m('label.f5', { for: 'diagram-equipment-auto-layout-datasets' }, 'Auto layout datasets'),
        m(ToggleSwitch, {
          id: 'diagram-equipment-auto-layout-datasets',
          checked: addDiagramEquipmentModal.autoLayoutDatasets() === true,
          ariaLabel: 'Auto layout datasets',
          onchange: function () {
            addDiagramEquipmentModal.autoLayoutDatasets(!addDiagramEquipmentModal.autoLayoutDatasets());
          }
        })
      ]),
      m('div.flex.items-center.justify-between', [
        m('label.f5', { for: 'diagram-equipment-auto-layout-markers' }, 'Auto layout markers'),
        m(ToggleSwitch, {
          id: 'diagram-equipment-auto-layout-markers',
          checked: addDiagramEquipmentModal.autoLayoutMarkers() === true,
          ariaLabel: 'Auto layout markers',
          onchange: function () {
            addDiagramEquipmentModal.autoLayoutMarkers(!addDiagramEquipmentModal.autoLayoutMarkers());
          }
        })
      ])
    ])
  ]);
};

addDiagramEquipmentModal.__testables = {
  buildPathLabel: buildPathLabel,
  buildPrimaryLabel: buildPrimaryLabel,
  getDiagramOwnerRid: getDiagramOwnerRid,
  normalizeResultRecords: normalizeResultRecords
};

module.exports = addDiagramEquipmentModal;
